import { createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { getInsightTagAdmin, updateInsightTag } from "@/lib/api/insight-tags.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { InsightsAdminNav } from "@/components/admin/InsightsAdminNav";
import { SlugField } from "@/components/admin/SlugField";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

function slugifyTag(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const Route = createFileRoute("/admin/insights/tags/$id")({
  loader: ({ params }) => getInsightTagAdmin({ data: { id: params.id } }),
  component: EditInsightTagPage,
});

function EditInsightTagPage() {
  const tag = Route.useLoaderData();
  if (!tag) throw notFound();

  const [name, setName] = useState(tag.name);
  const [slug, setSlug] = useState(tag.slug);
  const [status, setStatus] = useState(tag.status);
  const [featured, setFeatured] = useState(tag.featured);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await updateInsightTag({
        data: {
          id: tag.id,
          name,
          slug,
          status,
          featured,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Edit tag" />
      <InsightsAdminNav />
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <p className="text-xs text-muted-foreground">Renaming updates this tag on all linked articles.</p>
        </div>
        <SlugField
          title={name}
          slug={slug}
          onSlugChange={setSlug}
          onGenerate={() => Promise.resolve(slugifyTag(name))}
        />
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={featured} onCheckedChange={(v) => setFeatured(v === true)} />
          Show in Popular Topics on the insights page
        </label>
      </div>
      <AdminFormFooter cancelTo="/admin/insights/tags" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
